/**
 * ExamManagementTab — Admin management of real Red Hat exam pricing + monthly discounts.
 *
 * Section 1 — Exam Pricing: inline edit of INR/USD price + active toggle, add-exam modal
 * Section 2 — Monthly Discounts: month selector, per-exam discount % with computed
 *             discounted INR, carried-forward badge, warning banner when the current
 *             month has exams without a discount set.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Pencil, Check, X, Plus, RefreshCw, AlertTriangle, BadgePercent } from 'lucide-react';
import { API_BASE, getAdminToken, formatDate, Modal } from './adminUtils';

const currentMonthStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const authHeaders = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Bearer ${getAdminToken()}`,
});

const inr = (v) => `₹${Number(v || 0).toLocaleString('en-IN')}`;

const EMPTY_EXAM_FORM = {
  exam_code: '', exam_name: '', original_price_inr: '', original_price_usd: '', is_active: true,
};

export const ExamManagementTab = () => {
  const [exams, setExams] = useState([]);
  const [discounts, setDiscounts] = useState([]);
  const [reminderStatus, setReminderStatus] = useState(null);
  const [month, setMonth] = useState(currentMonthStr());
  const [loading, setLoading] = useState(true);

  // Inline pricing edit
  const [editingCode, setEditingCode] = useState(null);
  const [editForm, setEditForm] = useState({});

  // Inline discount edit
  const [editingDiscountCode, setEditingDiscountCode] = useState(null);
  const [discountValue, setDiscountValue] = useState('');

  // Add exam modal
  const [showAddModal, setShowAddModal] = useState(false);
  const [addForm, setAddForm] = useState(EMPTY_EXAM_FORM);
  const [saving, setSaving] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [examsRes, discountsRes, statusRes] = await Promise.all([
        fetch(`${API_BASE}/api/admin/exam-pricing`, { headers: authHeaders() }),
        fetch(`${API_BASE}/api/admin/exam-discounts?month=${month}`, { headers: authHeaders() }),
        fetch(`${API_BASE}/api/admin/exam-discount-reminder-status`, { headers: authHeaders() }),
      ]);
      if (examsRes.ok) setExams(await examsRes.json());
      if (discountsRes.ok) setDiscounts(await discountsRes.json());
      if (statusRes.ok) setReminderStatus(await statusRes.json());
    } catch (e) {
      console.error(e);
      toast.error('Failed to load exam data');
    } finally {
      setLoading(false);
    }
  }, [month]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ── Pricing handlers ─────────────────────────────────────────────────────
  const startEdit = (exam) => {
    setEditingCode(exam.exam_code);
    setEditForm({
      exam_code: exam.exam_code,
      exam_name: exam.exam_name,
      original_price_inr: exam.original_price_inr,
      original_price_usd: exam.original_price_usd,
      is_active: exam.is_active,
    });
  };

  const savePricing = async (examCode) => {
    setSaving(true);
    try {
      const payload = {
        exam_name: editForm.exam_name,
        original_price_inr: Number(editForm.original_price_inr),
        original_price_usd: Number(editForm.original_price_usd),
        is_active: editForm.is_active,
      };
      // Only send a rename when the code actually changed
      if (editForm.exam_code && editForm.exam_code !== examCode) {
        payload.new_exam_code = editForm.exam_code;
      }
      const res = await fetch(`${API_BASE}/api/admin/exam-pricing/${examCode}`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(typeof data.detail === 'string' ? data.detail : 'Update failed');
      }
      toast.success(`${examCode} updated`);
      setEditingCode(null);
      fetchAll();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const addExam = async (e) => {
    e.preventDefault();
    if (!addForm.exam_code || !addForm.exam_name || !addForm.original_price_inr || !addForm.original_price_usd) {
      toast.error('All fields are required');
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/exam-pricing`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          ...addForm,
          original_price_inr: Number(addForm.original_price_inr),
          original_price_usd: Number(addForm.original_price_usd),
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(typeof data.detail === 'string' ? data.detail : 'Create failed');
      }
      toast.success('Exam added');
      setShowAddModal(false);
      setAddForm(EMPTY_EXAM_FORM);
      fetchAll();
    } catch (e2) {
      toast.error(e2.message);
    } finally {
      setSaving(false);
    }
  };

  // ── Discount handlers ────────────────────────────────────────────────────
  const startDiscountEdit = (examCode, existing) => {
    setEditingDiscountCode(examCode);
    setDiscountValue(existing ? String(existing.discount_percent) : '');
  };

  const saveDiscount = async (examCode) => {
    const pct = Number(discountValue);
    if (Number.isNaN(pct) || pct < 0 || pct > 100) {
      toast.error('Discount must be between 0 and 100');
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/exam-discounts`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ exam_code: examCode, discount_percent: pct, valid_month: month }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(typeof data.detail === 'string' ? data.detail : 'Save failed');
      }
      toast.success(`${examCode} → ${pct}% for ${month}`);
      setEditingDiscountCode(null);
      fetchAll();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const discountFor = (examCode) => discounts.find((d) => d.exam_code === examCode);
  const missingCount = month === currentMonthStr() && reminderStatus
    ? (reminderStatus.missing_exams || []).length
    : exams.filter((e) => e.is_active && !discountFor(e.exam_code)).length;

  const inputCls = 'w-full bg-slate-900/60 border border-slate-600 rounded-lg px-3 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500/50';
  const smallInputCls = 'w-24 bg-slate-900/60 border border-slate-600 rounded px-2 py-1 text-sm text-white focus:outline-none focus:ring-1 focus:ring-orange-500/50';

  if (loading && exams.length === 0) {
    return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" /></div>;
  }

  return (
    <div className="space-y-8">
      {/* Warning banner — current month coverage */}
      {missingCount > 0 && (
        <div className="flex items-center gap-3 bg-orange-500/10 border border-orange-500/40 rounded-xl px-4 py-3">
          <AlertTriangle className="w-5 h-5 text-orange-400 shrink-0" />
          <p className="text-sm text-orange-300">
            ⚠️ {missingCount} exam(s) have no discount set for this month — last month's discount will be carried forward
          </p>
        </div>
      )}

      {/* ── Section 1: Exam Pricing ─────────────────────────────────────── */}
      <div className="bg-slate-800/60 border border-slate-700/50 rounded-2xl p-6">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h2 className="text-lg font-bold text-white">Exam Pricing</h2>
            <p className="text-sm text-slate-400 mt-0.5">{exams.length} exams · {exams.filter((e) => e.is_active).length} active</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={fetchAll} className="p-2 text-slate-400 hover:text-white rounded-lg border border-slate-600 transition-colors" title="Refresh">
              <RefreshCw className="w-4 h-4" />
            </button>
            <button onClick={() => setShowAddModal(true)} className="flex items-center gap-1.5 bg-orange-600 hover:bg-orange-500 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors">
              <Plus className="w-4 h-4" /> Add New Exam
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-slate-400 border-b border-slate-700/50">
                <th className="text-left py-3 px-2">Exam Name</th>
                <th className="text-left py-3 px-2">Code</th>
                <th className="text-left py-3 px-2">Original INR</th>
                <th className="text-left py-3 px-2">Original USD</th>
                <th className="text-left py-3 px-2">Active</th>
                <th className="text-right py-3 px-2">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {exams.map((exam) => {
                const isEditing = editingCode === exam.exam_code;
                return (
                  <tr key={exam.exam_code} className="hover:bg-slate-800/30 transition-colors">
                    <td className="py-3 px-2 text-white font-medium">
                      {isEditing ? (
                        <input type="text" maxLength={100} value={editForm.exam_name}
                          onChange={(e) => setEditForm((f) => ({ ...f, exam_name: e.target.value }))}
                          className={smallInputCls} />
                      ) : exam.exam_name}
                    </td>
                    <td className="py-3 px-2 text-slate-400 font-mono text-xs">
                      {isEditing ? (
                        <input type="text" maxLength={50} value={editForm.exam_code}
                          onChange={(e) => setEditForm((f) => ({ ...f, exam_code: e.target.value.toUpperCase() }))}
                          className={`${smallInputCls} font-mono`} />
                      ) : exam.exam_code}
                    </td>
                    <td className="py-3 px-2 text-slate-300">
                      {isEditing ? (
                        <input type="number" min="1" step="any" value={editForm.original_price_inr}
                          onChange={(e) => setEditForm((f) => ({ ...f, original_price_inr: e.target.value }))}
                          className={smallInputCls} />
                      ) : inr(exam.original_price_inr)}
                    </td>
                    <td className="py-3 px-2 text-slate-300">
                      {isEditing ? (
                        <input type="number" min="1" step="any" value={editForm.original_price_usd}
                          onChange={(e) => setEditForm((f) => ({ ...f, original_price_usd: e.target.value }))}
                          className={smallInputCls} />
                      ) : `$${Number(exam.original_price_usd || 0).toLocaleString('en-US')}`}
                    </td>
                    <td className="py-3 px-2">
                      {isEditing ? (
                        <button type="button" onClick={() => setEditForm((f) => ({ ...f, is_active: !f.is_active }))}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${editForm.is_active ? 'bg-emerald-600' : 'bg-slate-700'}`}>
                          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${editForm.is_active ? 'translate-x-6' : 'translate-x-1'}`} />
                        </button>
                      ) : (
                        <span className={`text-xs px-2 py-1 rounded-full ${exam.is_active ? 'bg-emerald-500/20 text-emerald-300' : 'bg-slate-700 text-slate-400'}`}>
                          {exam.is_active ? 'Active' : 'Inactive'}
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-2 text-right">
                      {isEditing ? (
                        <div className="flex justify-end gap-1">
                          <button onClick={() => savePricing(exam.exam_code)} disabled={saving} className="p-1.5 text-emerald-400 hover:bg-emerald-500/20 rounded" title="Save"><Check className="w-4 h-4" /></button>
                          <button onClick={() => setEditingCode(null)} className="p-1.5 text-slate-400 hover:bg-slate-700 rounded" title="Cancel"><X className="w-4 h-4" /></button>
                        </div>
                      ) : (
                        <button onClick={() => startEdit(exam)} className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-700 rounded" title="Edit"><Pencil className="w-4 h-4" /></button>
                      )}
                    </td>
                  </tr>
                );
              })}
              {exams.length === 0 && (
                <tr><td colSpan={6} className="text-slate-500 text-center py-8">No exams configured yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Section 2: Monthly Discounts ────────────────────────────────── */}
      <div className="bg-slate-800/60 border border-slate-700/50 rounded-2xl p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
          <div>
            <h2 className="text-lg font-bold text-white flex items-center gap-2">
              <BadgePercent className="w-5 h-5 text-orange-400" /> Monthly Discounts
            </h2>
            <p className="text-sm text-slate-400 mt-0.5">Discount % applied to the public exam booking page for the selected month</p>
          </div>
          <input type="month" value={month} onChange={(e) => setMonth(e.target.value)}
            className="bg-slate-900/60 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-orange-500/50" />
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-slate-400 border-b border-slate-700/50">
                <th className="text-left py-3 px-2">Exam</th>
                <th className="text-left py-3 px-2">Discount %</th>
                <th className="text-left py-3 px-2">Discounted INR</th>
                <th className="text-left py-3 px-2">Last Updated</th>
                <th className="text-left py-3 px-2">Carried Forward</th>
                <th className="text-right py-3 px-2">Edit</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {exams.filter((e) => e.is_active).map((exam) => {
                const discount = discountFor(exam.exam_code);
                const pct = discount ? Number(discount.discount_percent) : 0;
                const discounted = Math.round(Number(exam.original_price_inr || 0) * (1 - pct / 100));
                const isEditing = editingDiscountCode === exam.exam_code;
                return (
                  <tr key={exam.exam_code} className="hover:bg-slate-800/30 transition-colors">
                    <td className="py-3 px-2 text-white font-medium">{exam.exam_name}</td>
                    <td className="py-3 px-2">
                      {isEditing ? (
                        <input type="number" min="0" max="100" step="any" value={discountValue}
                          onChange={(e) => setDiscountValue(e.target.value)} autoFocus
                          className="w-20 bg-slate-900/60 border border-slate-600 rounded px-2 py-1 text-sm text-white focus:outline-none focus:ring-1 focus:ring-orange-500/50" />
                      ) : discount ? (
                        <span className="text-orange-400 font-bold">{pct}%</span>
                      ) : (
                        <span className="text-red-400 text-xs">Not set</span>
                      )}
                    </td>
                    <td className="py-3 px-2 text-slate-300">{discount ? inr(discounted) : inr(exam.original_price_inr)}</td>
                    <td className="py-3 px-2 text-slate-400 text-xs">{discount ? formatDate(discount.updated_at) : '—'}</td>
                    <td className="py-3 px-2">
                      {discount?.carried_forward && (
                        <span className="text-xs px-2 py-1 rounded-full bg-amber-500/20 text-amber-300">Carried Forward</span>
                      )}
                    </td>
                    <td className="py-3 px-2 text-right">
                      {isEditing ? (
                        <div className="flex justify-end gap-1">
                          <button onClick={() => saveDiscount(exam.exam_code)} disabled={saving} className="p-1.5 text-emerald-400 hover:bg-emerald-500/20 rounded" title="Save"><Check className="w-4 h-4" /></button>
                          <button onClick={() => setEditingDiscountCode(null)} className="p-1.5 text-slate-400 hover:bg-slate-700 rounded" title="Cancel"><X className="w-4 h-4" /></button>
                        </div>
                      ) : (
                        <button onClick={() => startDiscountEdit(exam.exam_code, discount)} className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-700 rounded" title="Edit"><Pencil className="w-4 h-4" /></button>
                      )}
                    </td>
                  </tr>
                );
              })}
              {exams.filter((e) => e.is_active).length === 0 && (
                <tr><td colSpan={6} className="text-slate-500 text-center py-8">No active exams.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add exam modal */}
      <Modal show={showAddModal} onClose={() => setShowAddModal(false)} title="Add New Exam">
        <form onSubmit={addExam} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-slate-400 mb-1.5">Exam Code * <span className="text-slate-600">(uppercased)</span></label>
              <input type="text" value={addForm.exam_code} maxLength={50} required placeholder="e.g. EXAM_EX188"
                onChange={(e) => setAddForm((f) => ({ ...f, exam_code: e.target.value.toUpperCase() }))} className={inputCls} />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-400 mb-1.5">Exam Name *</label>
              <input type="text" value={addForm.exam_name} maxLength={100} required placeholder="e.g. EX188 (OpenShift Dev)"
                onChange={(e) => setAddForm((f) => ({ ...f, exam_name: e.target.value }))} className={inputCls} />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-400 mb-1.5">Original Price INR *</label>
              <input type="number" min="1" step="any" value={addForm.original_price_inr} required placeholder="23600"
                onChange={(e) => setAddForm((f) => ({ ...f, original_price_inr: e.target.value }))} className={inputCls} />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-400 mb-1.5">Original Price USD *</label>
              <input type="number" min="1" step="any" value={addForm.original_price_usd} required placeholder="500"
                onChange={(e) => setAddForm((f) => ({ ...f, original_price_usd: e.target.value }))} className={inputCls} />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => setAddForm((f) => ({ ...f, is_active: !f.is_active }))}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${addForm.is_active ? 'bg-emerald-600' : 'bg-slate-700'}`}>
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${addForm.is_active ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
            <span className="text-sm text-slate-400">{addForm.is_active ? 'Active' : 'Inactive'}</span>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={() => setShowAddModal(false)} className="text-slate-400 hover:text-white text-sm font-medium px-4 py-2 rounded-lg border border-slate-600 transition-colors">Cancel</button>
            <button type="submit" disabled={saving} className="bg-orange-600 hover:bg-orange-500 disabled:bg-slate-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors">
              {saving ? 'Saving…' : 'Add Exam'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default ExamManagementTab;
